import { createConnector } from 'wagmi';
import { getAddress, type Address } from 'viem';
import { getFarcasterEthereumProvider } from '../lib/farcasterMiniApp';

type Eip1193 = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, listener: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, listener: (...args: unknown[]) => void) => void;
};

/**
 * Wagmi connector that uses the Farcaster Mini App host wallet.
 * No-ops (connect throws) on the open web.
 */
export function farcasterMiniAppConnector() {
  return createConnector((config) => {
    let provider: Eip1193 | null = null;

    const getProvider = async () => {
      if (provider) return provider;
      provider = (await getFarcasterEthereumProvider()) as Eip1193 | null;
      return provider;
    };

    return {
      id: 'farcasterMiniApp',
      name: 'Farcaster',
      type: 'farcasterMiniApp',
      async connect() {
        const p = await getProvider();
        if (!p) throw new Error('Not in a Farcaster Mini App');
        const accounts = ((await p.request({ method: 'eth_requestAccounts' })) as string[]).map(
          (a) => getAddress(a),
        );
        const chainIdHex = (await p.request({ method: 'eth_chainId' })) as string;
        const chainId = Number(chainIdHex);
        return { accounts: accounts as readonly Address[], chainId } as never;
      },
      async disconnect() {
        provider = null;
      },
      async getAccounts() {
        const p = await getProvider();
        if (!p) return [];
        const accounts = (await p.request({ method: 'eth_accounts' })) as string[];
        return accounts.map((a) => getAddress(a));
      },
      async getChainId() {
        const p = await getProvider();
        if (!p) return config.chains[0].id;
        const chainIdHex = (await p.request({ method: 'eth_chainId' })) as string;
        return Number(chainIdHex);
      },
      async getProvider() {
        return (await getProvider()) as never;
      },
      async isAuthorized() {
        try {
          const accounts = await this.getAccounts();
          return accounts.length > 0;
        } catch {
          return false;
        }
      },
      async switchChain({ chainId }) {
        const p = await getProvider();
        const chain = config.chains.find((c) => c.id === chainId);
        if (!p || !chain) throw new Error('Cannot switch chain');
        await p.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${chainId.toString(16)}` }],
        });
        return chain;
      },
      onAccountsChanged(accounts) {
        if (accounts.length === 0) config.emitter.emit('disconnect');
        else config.emitter.emit('change', { accounts: accounts.map((a) => getAddress(a)) });
      },
      onChainChanged(chain) {
        const chainId = Number(chain);
        config.emitter.emit('change', { chainId });
      },
      onDisconnect() {
        config.emitter.emit('disconnect');
      },
    };
  });
}
